import React from 'react';
import { useHistory } from 'react-router-dom';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import { Button } from '@components/atoms/Button';
import { AssistantChat } from '@components/organisms/Assistant/AssistantChat';
import { ConversationList } from '@components/organisms/Assistant/ConversationList';
import { useAssistantSession } from '@components/organisms/Assistant/useAssistantSession';
import { useMyAssistantConversations } from '@hooks/api/assistant';
import { useProject } from '@hooks/api/projects/useProject';
import { useTranslation } from '@hooks/useTranslation';

const NotFoundPanel: React.FC<{ onNew: () => void }> = ({ onNew }) => {
  const t = useTranslation('assistant');
  return (
    <div
      className="flex flex-col h-full items-center justify-center text-center gap-3 rounded-lg border border-gray-200 bg-white p-6"
      data-testid="assistant-not-found">
      <SearchOffOutlinedIcon sx={{ fontSize: 40 }} className="text-gray-400" />
      <div className="text-base font-semibold">{t.notFoundTitle}</div>
      <div className="text-sm text-gray-600">{t.notFoundBody}</div>
      <Button type="button" variant="primary" onClick={onNew}>
        {t.startNewChat}
      </Button>
    </div>
  );
};

/**
 * Sprint 37, story-196. `/assistant` and `/assistant/:conversationId` (one Route, so the page
 * instance survives the `replace` after the first send): conversation list on the left, chat on
 * the right. Role gating is the route's (`canAccess('ASSISTANT')` in `App.tsx`).
 */
export const AssistantPage: React.FC = () => {
  const session = useAssistantSession();
  const history = useHistory();
  const conversations = useMyAssistantConversations();

  // `?project=<id>` naming a project the (role-scoped) project query cannot return is the
  // not-found state, not a crash.
  const projectQuery = useProject(session.conversationId ? undefined : session.projectParam);
  const projectMissing = !!session.projectParam && projectQuery.isError;

  return (
    <div className="flex gap-4 w-full h-[calc(100vh-3rem)] min-h-0" data-testid="assistant-page">
      <aside className="w-60 xl:w-72 flex-shrink-0 min-h-0">
        <ConversationList
          conversations={conversations.data ?? []}
          activeId={session.conversationId}
          loading={conversations.isLoading}
          error={conversations.isError}
          onRetry={() => void conversations.refetch()}
          onSelect={session.selectConversation}
          onNew={session.startNew}
          onDelete={(id) => void session.deleteConversation(id)}
        />
      </aside>
      <section className="flex-1 min-w-0 min-h-0">
        {session.notFound || projectMissing ? (
          <NotFoundPanel onNew={() => history.push('/assistant')} />
        ) : (
          <AssistantChat session={session} />
        )}
      </section>
    </div>
  );
};

export default AssistantPage;
